<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import IconBox from '@uiKit/components/IconBox.vue'
import type { FundingMethod } from '../constants/fiatWalletConstants'

interface Props {
  method: FundingMethod
  selected?: boolean
}

withDefaults(defineProps<Props>(), {
  selected: false,
})

const emit = defineEmits<{
  (e: 'select', id: FundingMethod['id']): void
}>()
</script>

<template>
  <Box
    is="button"
    align="center"
    gap="4"
    :class="[
      'fmo',
      { 'fmo--selected': selected && method.available, 'fmo--disabled': !method.available },
    ]"
    type="button"
    :disabled="!method.available"
    :aria-pressed="selected"
    @click="emit('select', method.id)"
  >
    <IconBox
      :icon="method.icon"
      size="xl"
      bg="var(--p-surface-100)"
      icon-color="var(--p-text-muted-color)"
      class="fmo__icon"
    />

    <Box direction="col" gap="1" grow class="fmo__mid">
      <Text type="heading" weight="medium" align="left" is="span" class="fmo__title">
        {{ method.title }}
      </Text>
      <Text type="body" color="muted" align="left" is="span" class="fmo__subtitle">
        {{ method.subtitle }}
      </Text>
    </Box>

    <Box align="center" class="fmo__tag">
      <Text type="body" weight="medium" color="muted" is="span">{{ method.tag }}</Text>
    </Box>
  </Box>
</template>

<style scoped>
.fmo {
  width: 100%;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: 12px;
  padding: 1rem 1.125rem;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  transition:
    border-color 0.15s,
    background 0.15s;
}

.fmo:hover:not(:disabled) {
  border-color: var(--p-primary-200);
  background: var(--p-primary-50);
}

.fmo:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

/* Design draws the selected method with a 1.5px violet edge. Compensate the
   extra half-pixel with padding so the row doesn't shift on selection. */
.fmo--selected {
  border: 1.5px solid var(--p-primary-600);
  padding: calc(1rem - 0.5px) calc(1.125rem - 0.5px);
}

.fmo--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.fmo__mid {
  min-width: 0;
}

.fmo__title,
.fmo__subtitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.fmo__tag {
  flex-shrink: 0;
  background: var(--p-surface-100);
  border-radius: 6px;
  padding: 0.25rem 0.625rem;
}

/* Below tablet the tag can't share the line with the label — fold it under the
   text and let the title wrap instead of truncating to two characters. */
@media (max-width: 992px) {
  .fmo {
    display: grid;
    grid-template-columns: 46px minmax(0, 1fr);
    grid-template-areas:
      'icon mid'
      'icon tag';
    column-gap: 1rem;
    row-gap: 0.5rem;
    align-items: center;
  }

  .fmo :deep(.icon-box) {
    grid-area: icon;
    align-self: start;
  }

  .fmo__mid {
    grid-area: mid;
  }

  .fmo__tag {
    grid-area: tag;
    justify-self: start;
  }

  .fmo__title,
  .fmo__subtitle {
    white-space: normal;
  }
}
</style>
